import React, { useEffect, useRef, useState } from 'react'
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../../ui/alert-dialog'
import { Button } from '../../ui/button'
import { Input } from '../../ui/input'
import { Label } from '../../ui/label'
import { useMutation, useQuery } from 'react-query'
import axios from 'src/axios/axios'
import useAuth from 'src/hooks/useAuth'
import { Loader2 } from 'lucide-react'
import { Textarea } from 'src/components/ui/textarea'
import toast from 'react-hot-toast'
import { Item, MultiSelect } from 'src/components/ui/multiselect'
import { useTranslation } from 'react-i18next'
import autoAnimate from '@formkit/auto-animate'
import { Switch } from 'src/components/ui/switch'

type Props = {
    onSuccess?: () => void
    docid: number
}

const AddMessage = ({onSuccess, docid}:Props) => {
    const { t } = useTranslation()
    const { auth } = useAuth()
    const [title, settitle] = useState('');
    const [message, setmessage] = useState('');
    const [selected_users, setselected_users] = useState<Item[]>([]);
    const [auto_close, setauto_close] = useState<boolean>(false);

    const { mutate, reset, isLoading } = useMutation({
        mutationFn: async () => {
            try {
                let body = {
                    token: auth.data?.token,
                    docid: docid,
                    title: title,
                    message: message,
                    users: selected_users.map(u=>({username:u.value})),
                    state: auto_close ? 2 : 1
                }
                const { data } = await axios.post(`communications/header/add`, body)
                if(data.errorcode!==0){ throw new Error(data.message) }

                reset();
                settitle(''); setmessage(''); setselected_users([]); setauto_close(false);
                onSuccess && onSuccess()
            } catch (error) {
                
            }
        },
        onError: (err) => {
            if(err instanceof Error) {
                toast(err.message)
            }
            else {
                toast('Er is een fout opgetreden')
            }
            
        }
    })

    const { data:colleges } = useQuery<ForwardUser[] | null>({
        queryKey: [`colleges`],
        queryFn: async () => {
            try {
                const { data } = await axios.get(`users/get`)
                if(data.errorcode!==0){ throw new Error(data.message) }
                if(!Array.isArray(data.object.users)){ throw new Error('users is no array') }
                
                let users = data.object.users.filter((i:ForwardUser)=>!i.blocked)
                return users
            } catch (error) {
                toast.error(`${t('Collegas ophalen mislukt')}`, {id:'colleges'})
                return null
            }
        }
    })

  return (
    <AlertDialog>
        <AlertDialogTrigger asChild><Button disabled={isLoading} size='sm' className='gap-2'>{isLoading && <Loader2 className='w-4 h-4 animate-spin'/>}{t('Nieuw bericht')}</Button></AlertDialogTrigger>
        <AlertDialogContent>
            <AlertDialogHeader>
                <AlertDialogTitle>{t('Bericht toevoegen')}</AlertDialogTitle>
                <AlertDialogDescription>{t('Vul onderstaande velden in om uw bericht op te slaan')}</AlertDialogDescription>
            </AlertDialogHeader>
            <div>
                <div>
                    <Label htmlFor='title'>{t('Titel')}</Label>
                    <Input value={title} onChange={({currentTarget})=>{settitle(currentTarget.value)}} placeholder={`${t('Titel')}...`} id='title'/>
                </div>
                <div>
                    <Label htmlFor='message'>{t('Bericht')}</Label>
                    <Textarea value={message} onChange={({currentTarget})=>{setmessage(currentTarget.value)}} placeholder={`${t('Typ uw bericht')}...`} id='message'/>
                </div>
                <div>
                    <Label htmlFor='message'>{t('Gebruikers')}</Label>
                    <MultiSelect onValueChange={(vs)=>{setselected_users(vs)}} selectedValue={selected_users} options={Array.isArray(colleges) ? colleges.map((c)=>({value:c.username, label:`${c.firstname} ${c.name}`})) : []} placeholder={`${t('Selecteer een collega')}...`}/>
                </div>
                <div className='flex items-center gap-2'>
                    <Switch checked={auto_close} onCheckedChange={(c)=>{setauto_close(c)}} id='auto_close'/>
                    <Label htmlFor='auto_close'>{t('Automatisch sluiten')}</Label>
                </div>
            </div>
            <AlertDialogFooter>
                <AlertDialogCancel>{t('Annuleren')}</AlertDialogCancel>
                <AlertDialogAction disabled={title.length < 1 || message.length < 1} onClick={()=>{mutate()}}>{t('Opslaan')}</AlertDialogAction>
            </AlertDialogFooter>
        </AlertDialogContent>
    </AlertDialog>
  )
}


export const AddMessageInline = ({onSuccess, docid}:Props) => {
    const { t } = useTranslation()

    const [open, setopen] = useState(false);

    const { auth } = useAuth()
    const [title, settitle] = useState('');
    const [message, setmessage] = useState('');
    const [selected_users, setselected_users] = useState<Item[]>([]);
    const [auto_close, setauto_close] = useState<boolean>(false);
    
    const parent = useRef(null)

    useEffect(() => {
        parent.current && autoAnimate(parent.current)
    }, [parent])


    const { mutate, reset, isLoading } = useMutation({
        mutationFn: async () => {
            try {
                let body = {
                    token: auth.data?.token,
                    docid: docid,
                    title: title,
                    message: message,
                    users: selected_users.map(u=>({username:u.value})),
                    state: auto_close ? 2 : 1
                }
                
                const { data } = await axios.post(`communications/header/add`, body)
                if(data.errorcode!==0){ throw new Error(data.message) }
                
                reset();
                settitle(''); setmessage(''); setselected_users([]); setauto_close(false);
                onSuccess && onSuccess()
            } catch (error) {
                
            }
        }
    })

    const { data:colleges } = useQuery<ForwardUser[] | null>({
        queryKey: [`colleges`],
        queryFn: async () => {
            try {
                const { data } = await axios.get(`users/get`)
                if(data.errorcode!==0){ throw new Error(data.message) }
                if(!Array.isArray(data.object.users)){ throw new Error('users is no array') }
                
                let users = data.object.users.filter((i:ForwardUser)=>!i.blocked)
                return users
            } catch (error) {
                toast.error(`${t('Collegas ophalen mislukt')}`, {id:'colleges'})
                return null
            }
        }
    })

    return (
        <div className='flex flex-col w-full gap-4' ref={parent}>
            {!open && <Button onClick={()=>{setopen(!open)}} disabled={isLoading} size='sm' className='gap-2'>{isLoading && <Loader2 className='w-4 h-4 animate-spin'/>}{t('Nieuw bericht')}</Button>}
            {open &&
            <div className='p-2 bg-white border rounded-sm shadow-sm'>
                <div>
                    <Label htmlFor='title'>{t('Titel')}</Label>
                    <Input value={title} onChange={({currentTarget})=>{settitle(currentTarget.value)}} placeholder={`${t('Titel')}...`} id='title'/>
                </div>
                <div>
                    <Label htmlFor='message'>{t('Bericht')}</Label>
                    <Textarea value={message} onChange={({currentTarget})=>{setmessage(currentTarget.value)}} placeholder={`${t('Typ uw bericht')}...`} id='message'/>
                </div>
                <div>
                    <Label htmlFor='message'>{t('Gebruikers')}</Label>
                    <MultiSelect onValueChange={(vs)=>{setselected_users(vs)}} selectedValue={selected_users} options={Array.isArray(colleges) ? colleges.map((c)=>({value:c.username, label:`${c.firstname} ${c.name}`})) : []} placeholder={`${t('Selecteer een collega')}...`}/>
                </div>
                <div className='flex items-center gap-2'>
                    <Switch checked={auto_close} onCheckedChange={(c)=>{setauto_close(c)}} id='auto_close'/>
                    <Label htmlFor='auto_close'>{t('Automatisch sluiten')}</Label>
                </div>
                <div className='flex items-center justify-end gap-2'>
                    <Button onClick={()=>{setopen(false)}} variant='outline'>{t('Annuleren')}</Button>
                    <Button disabled={title.length < 1 || message.length < 1} onClick={()=>{mutate(); setopen(false)}}>{t('Opslaan')}</Button>
                </div>
            </div>
            }
        </div>
    )
}

export default AddMessage